"use client";

import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Target, ArrowRight } from "lucide-react";

const BASELINE_HORMIGA_MENSUAL = 60_000; // ARS

function formatARS(n: number): string {
  return n.toLocaleString("es-AR");
}

export default function SimulatorSection() {
  const [pct, setPct] = useState<number>(40);
  const [hormigaMensual, setHormigaMensual] = useState<number>(BASELINE_HORMIGA_MENSUAL);

  const ahorroMensual = useMemo(
    () => Math.round(hormigaMensual * (pct / 100)),
    [pct, hormigaMensual],
  );
  const ahorroAnual = ahorroMensual * 12;
  const gastoActualAnual = hormigaMensual * 12;

  return (
    <section
      id="simulador"
      data-section="section-simulator"
      className="relative py-24 sm:py-36 border-y border-white/[0.05]"
      aria-label="Simulador de ahorro"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div data-particle-shield className="mb-14 max-w-3xl">
          <div className="text-[12px] tracking-[0.2em] uppercase text-white/40 mb-5 inline-flex items-center gap-2">
            <Target size={13} className="text-primary" />
            Cuentas claras
          </div>
          <h2 className="text-[clamp(36px,5.5vw,72px)] font-extrabold leading-[1.05] tracking-tight">
            ¿Cuánto ahorrás si{" "}
            <span className="text-primary">cortás un poco</span>?
          </h2>
          <p className="text-white/55 mt-5 text-lg sm:text-xl max-w-2xl leading-relaxed">
            Movélo y mirá los números: lo que dejes de gastar en hormiga
            este mes es lo que vas a tener guardado a fin de año. No es
            promesa de retorno, es cuenta directa.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          data-particle-shield
          className="surface-paper rounded-3xl p-6 sm:p-10 lg:p-12"
        >
          {/* Sliders row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 mb-10 lg:mb-14">
            <div>
              <div className="flex items-baseline justify-between mb-3">
                <label className="text-[12px] tracking-[0.18em] uppercase text-white/40">
                  Hormiga al mes
                </label>
                <span className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  ${formatARS(hormigaMensual)}
                </span>
              </div>
              <input
                type="range"
                min={10000}
                max={200000}
                step={5000}
                value={hormigaMensual}
                onChange={(e) => setHormigaMensual(Number(e.target.value))}
                className="w-full accent-primary"
                aria-label="Tus gastos hormiga al mes"
              />
              <div className="flex justify-between text-[11px] text-white/30 mt-2">
                <span>$10k</span>
                <span>$200k</span>
              </div>
            </div>

            <div>
              <div className="flex items-baseline justify-between mb-3">
                <label className="text-[12px] tracking-[0.18em] uppercase text-white/40">
                  Cuánto vas a cortar
                </label>
                <span className="text-2xl sm:text-3xl font-extrabold text-primary tracking-tight">
                  {pct}%
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={pct}
                onChange={(e) => setPct(Number(e.target.value))}
                className="w-full accent-primary"
                aria-label="Porcentaje que vas a cortar"
              />
              <div className="flex justify-between text-[11px] text-white/30 mt-2">
                <span>0%</span>
                <span>100%</span>
              </div>
            </div>
          </div>

          {/* Big result row: Antes → Después */}
          <div className="border-t border-white/[0.06] pt-10">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-10 items-center">
              {/* Antes */}
              <div className="text-center md:text-right">
                <div className="text-[11px] tracking-[0.2em] uppercase text-white/35 mb-2">
                  Hoy gastás al año
                </div>
                <div className="text-3xl sm:text-4xl font-extrabold text-white/55 line-through decoration-1 decoration-white/30 tracking-tight">
                  ${formatARS(gastoActualAnual)}
                </div>
              </div>

              {/* Arrow */}
              <div className="flex md:flex-col items-center justify-center text-primary">
                <ArrowRight
                  size={28}
                  className="rotate-90 md:rotate-0"
                  strokeWidth={1.5}
                />
              </div>

              {/* Después */}
              <div className="text-center md:text-left">
                <div className="text-[11px] tracking-[0.2em] uppercase text-primary mb-2">
                  En 12 meses guardás
                </div>
                <div className="text-[clamp(40px,7vw,84px)] font-black leading-[1] tracking-tight gradient-text">
                  ${formatARS(ahorroAnual)}
                </div>
              </div>
            </div>

            <p className="text-center text-sm text-white/45 mt-8 max-w-md mx-auto leading-relaxed">
              Sin invertir y sin promesas. Esto es solo lo que dejás de gastar.
              Cuando puedas invertirlo, va a rendir mucho más.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
